'use client';

/**
 * Правила, которые проваливаются чаще всего.
 *
 * Доля считается от `applied` — числа проверок, где правило дало вердикт
 * (`pass` + `fail`), **без** `skipped`: доля провалов от «не смогли
 * проверить» ничего не говорит об операторе.
 *
 * Удалённое правило показываем с пометкой, а не прячем: оценка обязана
 * оставаться читаемой после того, как правило убрали из чек-листа. Имя
 * берётся из снимка в самой оценке — «правило 7f3a…» в отчёте бесполезно.
 */

import React from 'react';
import { AlertTriangle, Archive } from 'lucide-react';
import { count, percent } from '@/lib/format';
import type { TopViolationsData } from '@/lib/api/types';

export default function TopViolationsList({ data }: { data: TopViolationsData }) {
  if (data.scoredCalls === 0) {
    return (
      <p className="text-xs text-slate-400 leading-relaxed py-8 text-center px-6">
        За период не оценено ни одного звонка, поэтому нарушений нет — не потому, что их не
        совершали, а потому, что проверка не проводилась.
      </p>
    );
  }

  if (data.items.length === 0) {
    return (
      <p className="text-xs text-slate-400 py-8 text-center">
        Ни одно правило не провалено на {count(data.scoredCalls)} оценённых звонках.
      </p>
    );
  }

  const worst = Math.max(...data.items.map((item) => item.failRate ?? 0), 0.0001);

  return (
    <div className="space-y-3">
      {data.items.map((item) => (
        <div key={item.ruleId}>
          <div className="flex items-baseline justify-between gap-3 mb-1">
            <span className="text-xs text-slate-700 truncate">
              {item.critical && (
                <AlertTriangle
                  size={11}
                  className="inline text-red-500 mr-1 -mt-0.5"
                  aria-label="критическое"
                />
              )}
              {item.name}
              {item.deleted && (
                <span
                  className="ml-1.5 text-[10px] text-slate-400 inline-flex items-center gap-0.5"
                  title="Правило удалено из чек-листа; имя взято из самой оценки"
                >
                  <Archive size={9} />
                  удалено
                </span>
              )}
              <span className="ml-1.5 text-[10px] text-slate-400">{item.scorecardName}</span>
            </span>
            <span className="text-xs font-semibold tabular-nums text-slate-700 flex-shrink-0">
              {percent(item.failRate)}
              <span
                className="ml-1.5 font-normal text-[10px] text-slate-400"
                title="Провалов из проверок, где правило дало вердикт"
              >
                {count(item.fails)} из {count(item.applied)}
              </span>
            </span>
          </div>
          <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all bg-gradient-to-r ${
                item.critical ? 'from-red-400 to-red-500' : 'from-amber-300 to-amber-400'
              }`}
              style={{ width: `${((item.failRate ?? 0) / worst) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
